/**
 * AuthShell — split-screen wrapper used by `/login` and `/accept-invitation`.
 *
 * Visual direction matches `design_handoff_super_agency/screens/01-login.html`
 * from the Task 040 design zip:
 *   - Cream form side on the left (RTL: visually right)
 *   - Dark poster on the right (RTL: visually left) at desktop
 *   - Brand wordmark `Super` + italic `agency` with butter swash underline
 *   - Footer copyright + visual foot links
 *
 * Mobile: poster hides via `@media (max-width: 920px)`, form takes full
 * viewport width.
 *
 * The component renders no auth logic — it only owns the layout, brand,
 * poster, and footer. Page-specific copy / fields / buttons come in via
 * `children`.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface AuthShellProps {
  readonly children: ReactNode;
}

export function AuthShell({ children }: AuthShellProps) {
  return (
    <main className="auth-wrap">
      <section className="auth-form-side">
        <a className="auth-brand" href="/" aria-label="Super agency">
          <span>Super</span>
          <em>agency</em>
        </a>

        <div className="auth-stage">
          <div className="auth-inner">{children}</div>
        </div>

        <footer className="auth-foot">
          <span>© Super agency 2026 — صُنع بحبّ في الرياض</span>
          <div className="auth-foot-links">
            {/* Legal documents remain hidden until Task 300 supplies approved copy. */}
            <Link to="/support">المساعدة</Link>
          </div>
        </footer>
      </section>

      <aside className="auth-poster" aria-hidden="true">
        <div className="auth-poster-top">الحاصلون على ثقة 24 علامة سعوديّة</div>

        <div className="auth-poster-mid">
          <p className="auth-poster-quote">
            خلال شهرين، <em>وصلنا</em> تضاعف والتفاعل
            <span className="auth-poster-u"> خلق قصص جديدة </span>
            مع عملائنا — بصدق، غيّر طريقة شغلنا.
          </p>
          <div className="auth-poster-byline">
            <span className="auth-poster-av">خ</span>
            <div>
              <div className="auth-poster-nm">خالد العتيبي</div>
              <div className="auth-poster-role">مدير تسويق · مطاعم السعد</div>
            </div>
          </div>
        </div>

        <div className="auth-poster-stats">
          <div className="auth-poster-stat">
            <b>4.3×</b>
            <span>متوسّط ROAS</span>
          </div>
          <div className="auth-poster-stat">
            <b>+32%</b>
            <span>وصول شهري</span>
          </div>
          <div className="auth-poster-stat">
            <b>24</b>
            <span>علامة نشطة</span>
          </div>
        </div>
      </aside>
    </main>
  );
}
